'use client';

import type { MultiValue } from 'react-select';
import { memo, useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { IoIosSearch } from 'react-icons/io';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MultiSelect } from '@eliadmin/core/src/components/ui/multi-select';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { fetchJobCategories } from '@eliadmin/core/src/api/jobportal/categories-api';

type Option = { value: string | number; label: string };

const EMPTY_ARRAY: never[] = [];
const CATEGORY_QUERY = { childs: false, page: 1, results: 100 } as const;
const NO_OPTIONS = () => 'Žiadne možnosti';
const MENU_PORTAL_TARGET = typeof document !== 'undefined' ? document.body : undefined;

const MULTISELECT_CLASS_NAMES = {
  control: () => 'h-full w-full px-4 bg-transparent hover:outline-none focus:outline-none',
  placeholder: () => 'text-[#666666] text-sm lg:text-base',
  menu: () => 'bg-white shadow-lg rounded-[6px] border border-border',
  menuList: () => 'py-1 bg-white rounded-[6px] overflow-y-auto max-h-[260px]',
  option: () => 'hover:bg-muted px-3 py-2 cursor-pointer text-sm',
} as const;

export const HeroSearchForm = memo(function HeroSearchForm() {
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const [keyword, setKeyword] = useState('');
  const [selectedLocations, setSelectedLocations] = useState<MultiValue<Option>>(EMPTY_ARRAY);
  const [selectedCategories, setSelectedCategories] = useState<MultiValue<Option>>(EMPTY_ARRAY);

  const { data: countriesData, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    staleTime: 5 * 60 * 1000,
  });

  const { data: categoriesData, isLoading: isCategoriesLoading } = useQuery({
    queryKey: [CacheKey.JOBPORTAL_CATEGORIES, locale],
    queryFn: () => fetchJobCategories({ locale, query: CATEGORY_QUERY }),
    staleTime: 5 * 60 * 1000,
  });

  const countryOptions = useMemo<Option[]>(
    () => countriesData?.data.map(c => ({ value: c.id, label: c.name })) ?? EMPTY_ARRAY,
    [countriesData],
  );

  const categoryOptions = useMemo<Option[]>(
    () => categoriesData?.data.map(c => ({ value: c.id, label: c.name })) ?? EMPTY_ARRAY,
    [categoriesData],
  );

  const handleKeywordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  }, []);

  const handleLocationChange = useCallback(
    (val: MultiValue<Option>) => setSelectedLocations(val),
    [],
  );

  const handleCategoryChange = useCallback(
    (val: MultiValue<Option>) => setSelectedCategories(val),
    [],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const params = new URLSearchParams();
      if (keyword.trim()) params.set('keyword', keyword.trim());
      if (selectedLocations.length > 0)
        params.set('countries', selectedLocations.map(o => String(o.value)).join(','));
      if (selectedCategories.length > 0)
        params.set('categories', selectedCategories.map(o => String(o.value)).join(','));
      router.push(`/ponuky?${params.toString()}`);
    },
    [keyword, selectedLocations, selectedCategories, router],
  );

  return (
    <form
      onSubmit={handleSubmit}
      className="relative flex w-full flex-col overflow-visible rounded-[12px] lg:flex-row lg:items-stretch lg:justify-between"
    >
      <div className="grid w-full flex-1 grid-cols-1 gap-4 rounded-t-[12px] bg-white p-3 md:gap-2 lg:grid-cols-3 lg:rounded-b-none lg:rounded-l-[12px] lg:rounded-r-none">
        <Input
          name="keyword"
          aria-label="Kľúčové slovo"
          value={keyword}
          onChange={handleKeywordChange}
          className={{ wrapper: 'col-span-1 w-full' }}
          placeholder="Kľúčové slovo"
          classNames={{ wrapper: 'border border-border rounded-[6px]', main: 'px-4' }}
        />
        <MultiSelect
          options={countryOptions}
          aria-label="Krajina"
          value={selectedLocations}
          onChange={handleLocationChange}
          placeholder="Krajina"
          noOptionsMessage={NO_OPTIONS}
          menuPortalTarget={MENU_PORTAL_TARGET}
          menuPosition="fixed"
          isLoading={isCountriesLoading}
          className="min-hh-[52px] w-full rounded-[6px] border border-border bg-white text-sm hover:cursor-pointer"
          classNamesConfig={MULTISELECT_CLASS_NAMES}
        />
        <MultiSelect
          options={categoryOptions}
          aria-label="Kategória"
          value={selectedCategories}
          onChange={handleCategoryChange}
          placeholder="Kategória"
          noOptionsMessage={NO_OPTIONS}
          menuPortalTarget={MENU_PORTAL_TARGET}
          menuPosition="fixed"
          isLoading={isCategoriesLoading}
          className="min-hh-[52px] w-full rounded-[6px] border border-border bg-white text-sm hover:cursor-pointer"
          classNamesConfig={MULTISELECT_CLASS_NAMES}
        />
      </div>
      <Button
        type="submit"
        className="h-[52px] w-full rounded-none rounded-b-[12px] sm:h-16 lg:h-auto lg:max-w-[250px] lg:rounded-b-none lg:rounded-r-[12px]"
      >
        <IoIosSearch size={28} />
        Vyhľadať
      </Button>
    </form>
  );
});
